// The strategies someone owns, and which of them Today prints.
//
// A strategy is a document (see engine/strategy.js): JSON with no code in it,
// which is exactly why it can be stored beside a bet record and handed back to
// the engine tomorrow. The built-ins live in the catalogue and are read-only;
// a tuned copy is a FORK — a new id, a `baseId` pointing at what it came from,
// and `builtin: false` — so a built-in can be improved in a later build without
// silently rewriting somebody's saved one.
//
// EVERY STRATEGY RUNS, AND EVERY ONE OF THEM PRINTS UNLESS YOU SAY OTHERWISE.
// The pad used to hold four slots and a strategy had to be put on it; the four
// were an arithmetic nobody asked for, and choosing between them was a decision
// the app could not help anybody make. So the only choice left is Show or Hide,
// the stored list is the HIDDEN ones — a new strategy in a later build shows up
// rather than staying invisible until somebody goes looking — and the last
// visible one cannot be hidden, because Today has to print something.
//
// ONE-WAY IMPORT. This module reads and writes `store.js`'s risk profile;
// store.js does NOT import this one. The state they share — the saved
// strategies, and what a strategy document is — lives in the leaf module
// `strategy-state.js`, which imports nothing. See the note there for what a
// cycle here actually cost.

import { computed } from 'vue';
import { records } from '../../core/storage.js';
import { isDemo } from '../../core/demo.js';
import { fnv1a } from '../engine/hash.js';
import { validateStrategy } from '../engine/strategy.js';
import { CATALOGUE, SHARP_ANCHOR, findStrategy } from '../engine/strategies/catalogue.js';
import { canonicalStrategyId } from '../engine/strategies/aliases.js';
import { flags } from '../flags.js';
import { hiddenIds, saveRiskProfile } from '../../store.js';
import { myStrategies, toDocument, byName, STRATEGY_ENTITY } from './strategy-state.js';

// The shared pieces, re-exported so a screen has one place to import from.
export { myStrategies, toDocument, STRATEGY_ENTITY };

/** Six base-36 characters from a string — enough to keep two forks of one base apart. */
export function short6(seed) {
  return fnv1a(String(seed)).toString(36).padStart(6, '0').slice(-6);
}

// --- what is saved -----------------------------------------------------------

/**
 * Every strategy there is: the built-ins, then yours.
 *
 * Sharp Anchor is the one that comes and goes. It is parked rather than
 * deleted — nothing on the AU board it is anchored on carries Betfair or
 * Pinnacle — and Labs' `SHARP_ANCHOR` flag puts it back here, where it can be
 * read and forked. Only here: Today's own list is `store.js`'s
 * `allStrategyDocs`, which is the catalogue plus what you saved, so the way to
 * its tickets is a fork of it, which is a saved strategy like any other.
 */
export const allStrategies = computed(() => [
  ...CATALOGUE,
  ...(flags.SHARP_ANCHOR ? [SHARP_ANCHOR] : []),
  ...myStrategies.value,
]);

/**
 * A strategy by id, built-in or saved, or null.
 *
 * Three tries, in the order a caller means them. The id as written wins, so a
 * saved strategy is never shadowed. Then the id a retired one became, which is
 * what makes a trail row written under Optimal Legs answer "Market edge". Then
 * the retired documents themselves, so a name still resolves for the parked
 * ones — a bet placed under Sharp Anchor is still a bet under Sharp Anchor,
 * whatever Today prints this month.
 */
export function findAny(id) {
  const list = allStrategies.value;
  return list.find((s) => s.id === id)
    ?? list.find((s) => s.id === canonicalStrategyId(id))
    ?? findStrategy(id)
    ?? null;
}

/** Read the saved strategies out of IndexedDB. Safe to call more than once. */
export async function loadStrategies() {
  if (isDemo.value) {
    myStrategies.value = [];
    return myStrategies.value;
  }
  try {
    const rows = await records.all();
    myStrategies.value = rows.filter((r) => r?.entity === STRATEGY_ENTITY).sort(byName);
  } catch {
    // Private mode, or no IndexedDB at all. The catalogue still works.
    myStrategies.value = [];
  }
  return myStrategies.value;
}

/**
 * Save one of yours. Validates the DOCUMENT first and throws with every
 * complaint joined, because a strategy that cannot be validated cannot be run
 * and storing it would only move the failure to tomorrow.
 *
 * In /demo it is kept in memory and nothing is written, the same as everything
 * else in /demo.
 */
export async function saveStrategy(doc) {
  const document = toDocument({ ...doc, builtin: false });
  const check = validateStrategy(document);
  if (!check.ok) throw new Error(check.errors.join('; '));

  const existing = myStrategies.value.find((s) => s.id === document.id);
  const row = {
    ...document,
    entity: STRATEGY_ENTITY,
    builtin: false,
    baseId: doc.baseId ?? existing?.baseId ?? null,
    createdAt: existing?.createdAt ?? new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };

  myStrategies.value = [...myStrategies.value.filter((s) => s.id !== row.id), row].sort(byName);
  if (!isDemo.value) await records.put({ ...row });
  return row;
}

/**
 * Delete one of yours. A built-in is refused.
 *
 * Its id comes off the hidden list with it. Nothing would print it either way,
 * but a list that kept accumulating ids of strategies that no longer exist is a
 * list that eventually refuses to hide anything, because the count of what is
 * visible would be wrong.
 */
export async function deleteStrategy(id) {
  const strategy = findAny(id);
  if (!strategy) return null;
  if (strategy.builtin) throw new Error('A built-in strategy cannot be deleted. Fork it instead.');

  myStrategies.value = myStrategies.value.filter((s) => s.id !== id);
  if (hiddenIds.value.includes(id)) saveRiskProfile({ hidden: hiddenIds.value.filter((x) => x !== id) });
  if (!isDemo.value) await records.delete(id);
  return strategy;
}

/** How a fork with the long-shot knob turned up says so in its own name. */
const SHOT_WORDS = ['no', 'one', 'two', 'three'];

/**
 * What a fork is CALLED.
 *
 * Normally the name the editor was given, and `<base> (mine)` when it was
 * given none. The exception is a tier fork with long shots on it: the editor
 * hands back the base's own name unless somebody retyped it, so a tuned copy
 * of Classic would sit on the strategies list called "Classic Four Ways",
 * beside Classic Four Ways, differing only in the one knob nobody can see from
 * there. The knob goes in the name.
 */
function forkLabel(base, name) {
  const asked = String(name ?? '').trim();
  const shots = base?.legRule?.type === 'tier' ? Math.trunc(base?.params?.longShots ?? 0) : 0;
  if (shots > 0 && (!asked || asked === base.name)) {
    return `${base.name}, ${SHOT_WORDS[shots] ?? shots} long shot${shots === 1 ? '' : 's'}`;
  }
  return asked || `${base.name} (mine)`;
}

/**
 * A tuned copy of `base`, under a new name. Deep-cloned, so editing the fork
 * cannot reach back into the catalogue object it came from.
 *
 * The WHOLE document comes across, `legRule` and `params` included — a copy of
 * Classic keeps the tier search and the long-shot knob it was tuned with, and
 * `runStrategy` routes it back to `runClassic` on the leg rule.
 */
export function forkStrategy(base, name) {
  const document = structuredClone(toDocument(base));
  const label = forkLabel(base, name);
  return {
    ...document,
    id: `${base.id}-${short6(`${label}${Date.now()}`)}`,
    name: label,
    version: 1,
    builtin: false,
    baseId: base.id,
  };
}

// --- shown and hidden --------------------------------------------------------

/**
 * The hidden list after showing or hiding `id`, and why it was refused if it
 * was: `{ hidden, refused: 'last' | 'unknown' | null }`.
 *
 * Pure, and it takes the catalogue as an argument, so the two rules worth
 * having can be tested without a database. Both of them are about hiding:
 *
 *   'unknown'  an id that is not a strategy. Hiding it would put a dead id in
 *              the profile that nothing can ever take out again — the screen
 *              that offers to show one only lists strategies that exist.
 *   'last'     it would leave Today with nothing to print. The app's answer to
 *              "what should I back today" cannot be a blank page, so the last
 *              visible strategy stays visible and the caller says why.
 *
 * Showing is never refused: it can only make Today print more, and taking an
 * id off the list is also how a stale one gets cleaned up.
 *
 * BOTH THE LIST AND THE ID ARE CANONICALISED FIRST, and before the 'unknown'
 * refusal rather than after: a profile that hid Optimal Legs means to hide the
 * strategy Optimal Legs became, and an id that is no longer in any catalogue
 * would otherwise be refused as unknown and left in the list forever.
 *
 * Idempotent, and it never touches the list it was given.
 */
export function nextHidden(hidden, rawId, shown, all) {
  // Deduped, because two retired ids can canonicalise onto one heir and a
  // profile carrying "market-edge" twice is a list that never empties.
  const current = [...new Set((Array.isArray(hidden) ? hidden : [])
    .filter((x) => x != null)
    .map(canonicalStrategyId))];
  const catalogue = Array.isArray(all) ? all : [];
  const id = canonicalStrategyId(rawId);

  if (shown) return { hidden: current.filter((x) => x !== id), refused: null };

  if (!catalogue.some((s) => s?.id === id)) return { hidden: [...current], refused: 'unknown' };
  // Already hidden: nothing changes, so there is nothing to refuse. Before the
  // count below, because a profile that arrived hiding everything would
  // otherwise answer 'last' to a tap that was not going to do anything anyway.
  if (current.includes(id)) return { hidden: [...current], refused: null };
  const left = catalogue.filter((s) => s?.id !== id && !current.includes(s?.id));
  if (!left.length) return { hidden: [...current], refused: 'last' };

  return { hidden: [...current, id], refused: null };
}

/** Does this strategy print on Today right now? `hiddenIds` is already canonical. */
export const isShown = (id) => !hiddenIds.value.includes(canonicalStrategyId(id));

/**
 * Show or hide one strategy, and save it. Answers the refusal — 'last',
 * 'unknown' or null — so the caller can say what happened rather than leaving
 * a switch to spring back with no explanation.
 */
export function setShown(id, shown) {
  const { hidden, refused } = nextHidden(hiddenIds.value, id, shown, allStrategies.value);
  if (!refused) saveRiskProfile({ hidden });
  return refused;
}

/** The switch: whatever it is now, make it the other thing. */
export const toggleShown = (id) => setShown(id, !isShown(id));
